import type { ListBuilderConfiguration } from '../types';

/**
 * Entity type value mapping for linked records
 */
const ENTITY_TYPE_VALUE_MAPPING = [
	{
		color: { text: '#3b82f6', bg: '#dbeafe' },
		value: 'client',
		displayText: 'Client',
	},
	{
		color: { text: '#8b5cf6', bg: '#ede9fe' },
		value: 'vendor',
		displayText: 'Vendor',
	},
	{
		color: { text: '#f59e0b', bg: '#fef3c7' },
		value: 'opportunity',
		displayText: 'Opportunity',
	},
	{
		color: { text: '#10b981', bg: '#d1fae5' },
		value: 'order',
		displayText: 'Order',
	},
	{
		color: { text: '#06b6d4', bg: '#cffafe' },
		value: 'quote',
		displayText: 'Quote',
	},
	{
		color: { text: '#22c55e', bg: '#dcfce7' },
		value: 'invoice',
		displayText: 'Invoice',
	},
	{
		color: { text: '#6b7280', bg: '#f3f4f6' },
		value: 'item',
		displayText: 'Item',
	},
	{
		color: { text: '#3b82f6', bg: '#dbeafe' },
		value: 'sourcing_request',
		displayText: 'Sourcing Request',
	},
];

/**
 * Issue Link Embedded list configuration
 * Shows records linked to a specific issue
 * Converted from JSON to TypeScript for type safety
 */
export const ISSUE_LINK_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-issue-links-embedded',
			cacheTTL: 300000,
			name: 'Linked Records',
			type: 'default',
			active: true,
			sortOrder: 0,
			columns: [
				{
					id: 'entity-type-column',
					fields: [
						{
							id: 'entity-type-badge',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'entityType', objectTable: 'issue_link' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
								valueMapping: ENTITY_TYPE_VALUE_MAPPING,
							},
							displayStyle: 'badge',
						},
					],
					headers: [{ id: 'h-entity-type', text: 'Type' }],
					width: 140,
					align: 'left',
					sortable: true,
					sortFields: [
						{
							direction: 'ASC',
							fieldName: 'entityType',
							objectTable: 'issue_link',
						},
					],
				},
				{
					id: 'entity-name-column',
					fields: [
						{
							id: 'entity-name',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'entityName', objectTable: 'issue_link' },
							},
							link: {
								type: 'callback',
								enabled: true,
								callbackName: 'view',
							},
							styleConfig: {
								size: 'sm',
								weight: 'medium',
								colorRule: { type: 'theme', themeColor: 'primary' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'entity-secondary-info',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'entitySecondaryInfo',
									objectTable: 'issue_link',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h-entity-name', text: 'Name' },
						{ id: 'h-entity-secondary', text: 'Details' },
					],
					width: 250,
					sortable: false,
				},
				{
					id: 'created-date-column',
					fields: [
						{
							id: 'created-date',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'createdDate', objectTable: 'issue_link' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
								formatOptions: { dateFormat: 'short' },
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h-created-date', text: 'Linked' }],
					width: 100,
					align: 'left',
					sortable: true,
					sortFields: [
						{
							direction: 'DESC',
							fieldName: 'createdDate',
							objectTable: 'issue_link',
						},
					],
				},
				{
					id: 'actions-column',
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								{
									id: 'view-action',
									label: 'View',
									iconName: 'external-link',
									callbackName: 'view',
									variant: 'primary',
									visibility: 'visible',
								},
								{
									id: 'unlink-action',
									label: 'Unlink',
									iconName: 'link-slash',
									callbackName: 'unlink',
									variant: 'danger',
									visibility: 'visible',
								},
							],
						},
					],
					headers: [{ id: 'h-actions', text: '' }],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			defaultSortId: 'column-created-date-column-desc',
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: ['issueLinkEntityType'],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/issues/:issueId/links-list',
			objectLabel: 'Linked Record',
			objectTable: 'issue_link',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'entityType', fieldLabel: 'Type' },
				{ fieldName: 'entityId', fieldLabel: 'Entity ID' },
				{ fieldName: 'entityName', fieldLabel: 'Name' },
				{ fieldName: 'entitySecondaryInfo', fieldLabel: 'Details' },
				{ fieldName: 'createdDate', fieldLabel: 'Linked Date' },
			],
		},
	],
	tableSorts: [
		{
			id: 'created-date-desc',
			name: 'Recently Linked',
			fields: [
				{
					fieldName: 'createdDate',
					objectTable: 'issue_link',
					direction: 'DESC',
				},
			],
		},
		{
			id: 'created-date-asc',
			name: 'Oldest First',
			fields: [
				{
					fieldName: 'createdDate',
					objectTable: 'issue_link',
					direction: 'ASC',
				},
			],
		},
		{
			id: 'entity-type-asc',
			name: 'Type (A-Z)',
			fields: [
				{
					fieldName: 'entityType',
					objectTable: 'issue_link',
					direction: 'ASC',
				},
			],
		},
	],
};
